import { fetchEarlierHistory, supportsHistory } from '../services/chart-history';
import { mergeHistory } from '../services/history-pagination.mjs';
import DataCube from '../helpers/datacube';
import { resolvePricePrecision } from './price-precision.mjs';
import Const from '../stuff/constants.js';
import { providerCapabilities } from './market-analysis.mjs';
import binance from '../services/binance_service';
import {
    isServerAlertConnector,
    alertExchangeFor,
    listAlerts,
    createAlert as createServerAlert,
    updateAlert as updateServerAlert,
    toggleAlert as toggleServerAlert,
    deleteAlert as deleteServerAlert,
} from '../services/alert_service';
import {
    clone,
    isDrawing,
    marketKey,
    candlesToCSV,
    replayData,
    normalizedComparison,
    evaluateAlerts,
    newPaperAccount,
    paperEquity,
    fillPaperOrder,
    processPaperOrders,
    workspaceSnapshot,
    validateWorkspace,
} from './model.mjs';
const read = (key, fallback) => {
    try {
        return JSON.parse(localStorage.getItem(key)) ?? fallback;
    } catch (_) {
        return fallback;
    }
};
const commands = [
    ['focus', 'Focus chart', 'fullscreen'],
    ['commands', 'Quick search', 'search', '⌘ K'],
    ['symbol', 'Search symbols', 'search', '/'],
    ['indicators', 'Indicators', 'compare'],
    ['alerts', 'Create price alert', 'alert', 'Alt A'],
    ['replay', 'Bar replay', 'replay'],
    ['layout', 'Chart layout', 'layout'],
    ['toggleVT', 'Toggle Visual Trading', 'layers'],
    ['vtManager', 'Manage Visual Trading indicators', 'layers'],
    ['objects', 'Object tree', 'objects'],
    ['workspaces', 'Save or load workspace', 'save'],
    ['compare', 'Compare symbols', 'compare'],
    ['orderflow', 'Binance order flow and footprint', 'trade'],
    ['backtest', 'Strategy backtest', 'compare'],
    ['history', 'Load earlier Binance history', 'download'],
    ['paper', 'Paper trading', 'trade'],
    ['buildStrategy', 'Build a strategy from this chart', 'objects'],
    ['bots', 'Open paper and live bots', 'trade'],
    ['settings', 'Chart settings', 'settings'],
    ['theme', 'Toggle theme', 'settings'],
    ['exportCSV', 'Export chart data', 'download'],
    ['snapshot', 'Download chart snapshot', 'camera'],
    ['fullscreen', 'Fullscreen', 'fullscreen'],
    ['undo', 'Undo drawings', 'undo', '⌘ Z'],
    ['redo', 'Redo drawings', 'redo', '⌘ ⇧ Z'],
].map(([action, label, icon, shortcut]) => ({ action, label, icon, shortcut }));
export default {
    data() {
        return {
            workspacePanel: null,
            workspaceFocused: false,
            workspaceName: '',
            workspaceSaved: false,
            workspaceLayout: 1,
            workspaceActivePane: 0,
            // Only true once the user has actually clicked a pane - keeps
            // the blue "selected" outline from showing on page load / in
            // single-chart view just because pane 0 is active by default.
            workspacePaneUserSelected: false,
            workspaceLegendButtons: ['display', 'settings', 'add', 'remove', 'up', 'down'],
            workspacePaneRevision: 0,
            workspacePaneCubes: {},
            workspaceSavedPanes: {},
            workspaceSyncRange: true,
            workspaceRangeBusy: false,
            // Explicit, user-driven toggle (not derived from current overlays) so
            // switching timeframes/symbols never silently re-enables Visual Trading
            // after the user turned it off, or vice versa.
            workspaceVTEnabled: read('tc_workspace_vt_enabled', true),
            workspacePreferences: read('tc_workspace_preferences', {
                up: '#26a69a',
                down: '#ef5350',
                grid: true,
                volume: true,
                priceLine: true,
                log: false,
                precision: null,
            }),
            workspaceItems: [],
            workspaceCloudBusy: false,
            workspaceCloudStatus: '',
            workspaceAlerts: [],
            workspaceAlertLog: [],
            workspaceServerAlerts: [],
            workspaceAlertCapabilities: { count: 0, maxAlerts: 10 },
            workspacePaper: newPaperAccount(),
            workspaceToast: null,
            workspaceComparing: false,
            workspaceTick: 0,
            workspaceHistory: [],
            workspaceHistoryIndex: -1,
            workspaceSecondaryTf: ['15m', '1H', '1D'],
            workspaceReplay: {
                active: false,
                playing: false,
                index: 1,
                total: 0,
                speed: 1,
                time: '',
            },
            workspaceReplayCube: null,
        };
    },
    computed: {
        workspaceScope() {
            return 'tc_workspace_' + (this.$clerk?.user?.id || 'local');
        },
        workspaceKey() {
            return marketKey(this);
        },
        workspaceSymbolInfo() {
            const selected = this.selectedSymbolInfo;
            if (selected && selected.symbol === this.symbol && selected.connector === this.connector) return selected.properties;
            const symbols = this.allSymbols || {};
            const direct = symbols[this.symbol] || symbols[this.symbol + ':' + this.exchange] || symbols[String(this.connector).toLowerCase() + ':' + this.symbol];
            if (direct) return direct;
            return Object.values(symbols).find(item => {
                const identity = item.instrument_key || item.fyToken || item.token || item.symbol || item.trading_symbol;
                const matches = String(identity) === String(this.pairId || this.symbol) || item.symbol === this.symbol || item.trading_symbol === this.symbol;
                const provider = item.type || item.exchange || '';
                const sameProvider = this.connector === 'Binance' ? /Crypto|Binance/.test(provider) : provider === this.connector || item.exch === this.exchange || item.exchange === this.exchange;
                return matches && sameProvider;
            }) || {};
        },
        workspacePricePrecision() {
            const saved = this.workspacePreferences;
            // Two decimals was the old global default, not a symbol setting.
            const override = saved.precisionMode === 'manual' || saved.precision !== 2 ? saved.precision : null;
            return resolvePricePrecision(this.workspaceSymbolInfo, this.workspaceCandles, override);
        },
        workspaceSource() {
            return this.chart[this.timeframe]?.data;
        },
        workspaceAllAlerts() {
            return [
                ...this.workspaceServerAlerts.map((a) => ({ ...a, source: 'server' })),
                ...this.workspaceAlerts.map((a) => ({ ...a, source: 'local' })),
            ];
        },
        workspaceCandles() {
            return this.workspaceSource?.chart?.data || [];
        },
        workspacePrice() {
            this.workspaceTick;
            const rows = this.workspaceReplay.active
                ? this.workspaceReplayCube?.data?.chart?.data
                : this.workspaceCandles;
            return rows?.length ? Number(rows[rows.length - 1][4]) : 0;
        },
        workspaceDisplayCube() {
            return this.workspaceReplay.active
                ? this.workspaceReplayCube
                : this.chart[this.timeframe];
        },
        workspaceQuote() {
            const rows = this.workspaceReplay.active
                ? this.workspaceReplayCube?.data?.chart?.data
                : this.workspaceCandles;
            const row = rows?.[rows.length - 1];
            return row || [];
        },
        workspaceObjects() {
            this.workspaceTick;
            const source = this.workspaceSelectedSource;
            return ['onchart', 'offchart'].flatMap((side) =>
                (source?.[side] || []).map((o, index) => ({
                    key: side + ':' + index,
                    name: o.name || o.type,
                    side,
                    drawing: isDrawing(o),
                    visible: o.settings?.display !== false,
                    locked: !!o.settings?.$locked,
                }))
            );
        },
        workspaceVTIndicators() {
            this.workspaceTick;
            return this.workspaceVTIndicatorList();
        },
        workspaceModel() {
            return {
                name: this.workspaceName,
                saved: this.workspaceSaved,
                layout: this.workspaceLayout,
                focused: this.workspaceFocused,
                syncRange: this.workspaceSyncRange,
                panel: this.workspacePanel,
                commands: commands.filter(command => providerCapabilities(this.connector).aggregateTrades || !['orderflow','history'].includes(command.action)),
                capabilities: providerCapabilities(this.connector),
                candles: this.workspaceCandles,
                objects: this.workspaceObjects,
                vtActive: this.workspaceVTEnabled,
                vtIndicators: this.workspaceVTIndicators,
                alerts: this.workspaceAllAlerts,
                alertLog: this.workspaceAlertLog,
                strategyDockOpen: this.strategyDockOpen,
                alertCapabilities: this.workspaceAlertCapabilities,
                serverAlerts: isServerAlertConnector(this.connector),
                workspaces: this.workspaceItems,
                cloudBusy: this.workspaceCloudBusy,
                cloudStatus: this.workspaceCloudStatus,
                paper: this.workspacePaper,
                equity: paperEquity(this.workspacePaper),
                key: this.workspaceKey,
                symbol: this.symbol,
                timeframe: this.timeframe,
                price: this.workspacePrice,
                barCount: this.workspaceCandles.length,
                replay: this.workspaceReplay,
                preferences: this.workspacePreferences,
                dark: this.night,
                toast: this.workspaceToast,
                comparing: this.workspaceComparing,
                canUndo: this.workspaceHistoryIndex > 0,
                canRedo:
                    this.workspaceHistoryIndex <
                    this.workspaceHistory.length - 1,
                comparisons: (this.workspaceSource?.onchart || [])
                    .filter((o) => o.settings?.$workspaceComparison)
                    .map((o) => ({
                        symbol: o.settings.$workspaceComparison,
                        color: o.settings.color,
                        count: o.data.length,
                    })),
            };
        },
        workspaceToolbarWidth() { return this.chart_config.TOOLBAR || Const.ChartConfig.TOOLBAR },
        workspaceToolbarView() {
            this.workspacePaneRevision;
            const panes = this.$refs.workspaceSecondary || [];
            const pane = (Array.isArray(panes) ? panes : [panes]).find(pane => pane.pane === this.workspaceActivePane);
            const view = this.workspaceActivePane === 0 ? this.$refs.tv : pane && pane.$refs.chart;
            if (!view) return null;
            // Keep Vue instances out of computed return-value unwrapping.
            return { id:view.id, decubed:view.decubed, chart_props:view.chart_props,
                chart_config:view.chart_config, $props:view.$props,
                custom_event:event => view.custom_event(event) };
        },
        workspaceSelectedSource() {
            return this.workspaceToolbarView?.$props.data?.data || this.workspaceDisplayCube?.data;
        },
        workspacePaneWidth() {
            return Math.floor(
                (this.chartWidth - (this.showDrawingTools && this.drawingRailExpanded ? this.workspaceToolbarWidth : 0)) /
                    (this.workspaceLayout === 1 || this.width <= 600 ? 1 : 2)
            );
        },
        workspacePaneHeight() {
            return Math.floor(
                this.chartHeight /
                    (this.width <= 600
                        ? this.workspaceLayout
                        : this.workspaceLayout === 4
                        ? 2
                        : 1)
            );
        },
    },
    methods: {
        workspacePaneKey(pane, tf) { return this.workspaceKey + ':pane:' + pane + ':' + tf },
        workspacePaneReady() { this.workspacePaneRevision++ },
        workspaceSelectPane(pane) {
            // A real click/focus on a pane - from now on it's fine to show
            // which one is selected.
            this.workspacePaneUserSelected = true;
            this.workspaceResetActivePane(pane);
        },
        // Programmatic pane reset (layout change, loading a workspace,
        // switching scope) - must NOT count as the user picking a pane,
        // or the blue outline would show up unasked.
        workspaceResetActivePane(pane = 0) {
            if (pane === this.workspaceActivePane) return;
            this.workspaceActivePane = pane;
            this.workspaceHistory = [];
            this.workspaceHistoryIndex = -1;
            this._workspaceLastHistory = null;
        },
        workspaceToolEvent(event) { this.workspaceToolbarView?.custom_event(event) },
        workspacePersist() {
            try {
                localStorage.setItem(
                    this.workspaceScope,
                    JSON.stringify({
                        items: this.workspaceItems,
                        alerts: this.workspaceAlerts,
                        alertLog: this.workspaceAlertLog,
                        paper: this.workspacePaper,
                    })
                );
            } catch (_) {
                this.workspaceNotify(
                    'Browser storage is full. Export your workspace.'
                );
            }
        },
        workspaceNotify(message, icon = 'save') {
            this.workspaceToast = { message, icon };
            clearTimeout(this._workspaceToastTimer);
            this._workspaceToastTimer = setTimeout(
                () => (this.workspaceToast = null),
                7000
            );
        },
        workspaceRecordAlertTriggers(alerts) {
            const entries=(Array.isArray(alerts)?alerts:[]).filter(alert=>alert.triggeredAt).map(alert=>({key:alert.id+':'+alert.triggeredAt, name:alert.name, symbol:alert.symbol, time:new Date(alert.triggeredAt).getTime(), price:alert.triggeredPrice ?? alert.target}));
            const fresh=entries.filter(entry=>!this.workspaceAlertLog.some(existing=>existing.key===entry.key));
            if(fresh.length){this.workspaceAlertLog=[...fresh,...this.workspaceAlertLog].sort((a,b)=>b.time-a.time).slice(0,200);this.workspacePersist()}
        },
        async workspaceRefreshServerAlerts() {
            if (!this.$clerk?.user?.id) return;
            const scope = this.workspaceScope;
            try {
                const { alerts, capabilities } = await listAlerts();
                if (scope !== this.workspaceScope) return;
                this.workspaceServerAlerts = Array.isArray(alerts) ? alerts : [];
                this.workspaceRecordAlertTriggers(alerts);
                if(capabilities)this.workspaceAlertCapabilities = capabilities;
            } catch (_) {
                // Silent: this poll runs in the background. The next explicit
                // create/edit/toggle/delete call will surface a real error.
            }
        },
        async workspaceCreateAlert(payload) {
            if (!Number.isFinite(payload.target) || payload.target <= 0)
                throw new Error('Enter a valid price.');
            if (isServerAlertConnector(this.connector)) {
                if (!this.$clerk?.user?.id)
                    throw new Error('Sign in to create a ' + this.connector + ' price alert.');
                const { alert, capabilities } = await createServerAlert({
                    symbol: this.symbol,
                    exchange: alertExchangeFor(this.connector),
                    condition: payload.condition,
                    target: payload.target,
                    drawing: payload.drawing || null,
                    name: payload.name,
                });
                this.workspaceServerAlerts.unshift(alert);
                this.workspaceAlertCapabilities = capabilities || {...this.workspaceAlertCapabilities,count:this.workspaceAlertCapabilities.count+1};
                this.workspaceNotify(
                    'Price alert created. Runs on the server and notifies by email/chat even if this tab is closed.',
                    'alert'
                );
                return;
            }
            this.workspaceAlerts.unshift({
                ...payload,
                id: Date.now() + '-' + Math.random(),
                name: payload.name || this.symbol + ' price alert',
                key: this.workspaceKey,
                symbol: this.symbol,
                enabled: true,
            });
            this.workspacePersist();
            this.workspaceNotify('Price alert created.', 'alert');
        },
        async workspaceSubmitEditAlert(payload) {
            if (!Number.isFinite(payload.target) || payload.target <= 0)
                throw new Error('Enter a valid price.');
            if (payload.source === 'server') {
                const { alert } = await updateServerAlert(payload.id, {
                    condition: payload.condition,
                    target: payload.target,
                    drawing: payload.drawing || null,
                    name: payload.name,
                });
                const index = this.workspaceServerAlerts.findIndex(
                    (a) => a.id === payload.id
                );
                if (index !== -1)
                    this.workspaceServerAlerts.splice(index, 1, alert);
            } else {
                const a = this.workspaceAlerts.find((x) => x.id === payload.id);
                if (a) {
                    a.condition = payload.condition;
                    a.target = payload.target;
                    a.name = payload.name || a.name;
                    a.drawing = payload.drawing || null;
                    delete a._observed;
                    a.enabled = true;
                    delete a.triggeredAt;
                    delete a.triggeredPrice;
                    this.workspacePersist();
                }
            }
            this.workspaceNotify('Price alert updated.', 'alert');
        },
        async workspaceToggleAlert({ id, source }) {
            if (source === 'server') {
                const { alert } = await toggleServerAlert(id);
                const index = this.workspaceServerAlerts.findIndex(
                    (a) => a.id === id
                );
                if (index !== -1)
                    this.workspaceServerAlerts.splice(index, 1, alert);
                return;
            }
            const a = this.workspaceAlerts.find((x) => x.id === id);
            if (a) {
                a.enabled = !a.enabled;
                delete a.triggeredAt;
                this.workspacePersist();
            }
        },
        async workspaceDeleteAlert({ id, source }) {
            if (source === 'server') {
                await deleteServerAlert(id);
                this.workspaceServerAlerts = this.workspaceServerAlerts.filter(
                    (a) => a.id !== id
                );
                return;
            }
            this.workspaceAlerts = this.workspaceAlerts.filter(
                (x) => x.id !== id
            );
            this.workspacePersist();
        },
        // Routed from the draggable alert line drawn on the chart itself
        // (AlertLinesLayer, mounted inside <tc>) - keeps that UI a thin
        // relay onto the same CRUD pipeline the side-panel form uses.
        onChartAlertAction({ type, alert, price }) {
            if (type === 'add') {
                this.workspacePanel = 'alerts';
                this.$nextTick(() =>
                    this.$refs.workspaceControls?.startAddAlert(price)
                );
                return;
            }
            if (type === 'edit') {
                this.workspacePanel = 'alerts';
                this.$nextTick(() =>
                    this.$refs.workspaceControls?.startEditAlert(alert)
                );
                return;
            }
            if (type === 'drag') {
                this.workspaceAction({
                    action: 'editAlert',
                    payload: {
                        id: alert.id,
                        source: alert.source,
                        condition: alert.condition,
                        target: price,
                        name: alert.name,
                    },
                });
                return;
            }
            if (type === 'delete') {
                this.workspaceAction({
                    action: 'deleteAlert',
                    payload: { id: alert.id, source: alert.source },
                });
            }
        },
        workspaceDataChanged() {
            this.workspaceTick++;
            this.workspaceSaved = false;
        },
        async workspaceAction({ action, payload }) {
            try {
                await this.workspaceDispatch(action, payload);
            } catch (error) {
                this.workspaceNotify(error.message, 'alert');
            }
        },
        workspaceDispatch(action, payload) {
            if(action==='strategies'){this.toggleStrategyDock();return;}
            if(action==='backtest'){this.openStrategyDock('results');return;}
            if (
                [
                    'commands',
                    'layout',
                    'objects',
                    'alerts',
                    'workspaces',
                    'compare',
                    'settings',
                    'paper',
                    'orderflow',
                    'backtest',
                    'date',
                    'vtManager',
                ].includes(action)
            ) {
                this.workspacePanel =
                    this.workspacePanel === action ? null : action;
                return;
            }
            const source = this.workspaceSelectedSource;
            switch (action) {
                case 'buildStrategy':
                    this.openStrategyBuilder();
                    return;
                case 'bots':
                    this.$router.push('/bots');
                    return;
                case 'focus':
                    this.workspaceFocused = !this.workspaceFocused;
                    this.$nextTick(() => this.refreshChartRender());
                    break;
                case 'close':
                    if (this.workspaceFocused) {
                        this.workspaceFocused = false;
                        this.$nextTick(() => this.refreshChartRender());
                    }
                    this.workspacePanel = null;
                    break;
                case 'dismissToast':
                    this.workspaceToast = null;
                    break;
                case 'history':
                    return this.workspaceLoadEarlier();
                case 'symbol':
                    this.workspacePanel = null;
                    this.onOpen();
                    break;
                case 'indicators':
                    this.workspacePanel = null;
                    this.openAddIndicator();
                    break;
                case 'theme':
                    this.toggleTheme();
                    break;
                case 'setLayout':
                    this.workspaceLayout = payload;
                    this.workspacePanel = null;
                    this.refreshChartRender();
                    break;
                case 'toggleVT':
                    this.workspaceToggleVT();
                    break;
                case 'toggleVTIndicator':
                    this.workspaceToggleVTIndicator(payload);
                    break;
                case 'dateRange': {
                    const start = Date.parse(payload.start + 'Z'),
                        end = Date.parse(payload.end + 'Z');
                    if (
                        !Number.isFinite(start) ||
                        !Number.isFinite(end) ||
                        start >= end
                    )
                        throw new Error(
                            'Choose an end after the start in UTC.'
                        );
                    if (!this.workspaceCandles.length)
                        throw new Error('Load chart data first.');
                    const first = this.workspaceCandles[0][0],
                        last =
                            this.workspaceCandles[
                                this.workspaceCandles.length - 1
                            ][0];
                    if (end < first || start > last)
                        throw new Error(
                            'This range is outside the loaded history.'
                        );
                    this.$refs.tv?.setRange(
                        Math.max(first, start),
                        Math.min(last, end)
                    );
                    this.workspacePanel = null;
                    break;
                }
                case 'syncRange':
                    this.workspaceSyncRange = payload;
                    break;
                case 'fullscreen':
                    if (document.fullscreenElement) document.exitFullscreen();
                    else this.$el.requestFullscreen?.();
                    break;
                case 'exportCSV':
                    if (!this.workspaceCandles.length)
                        throw new Error('Load chart data first.');
                    this.workspaceDownload(
                        candlesToCSV(
                            this.workspaceReplay.active
                                ? this.workspaceReplayCube.data.chart.data
                                : this.workspaceCandles
                        ),
                        this.symbol + '-' + this.timeframe + '.csv',
                        'text/csv'
                    );
                    break;
                case 'snapshot':
                    this.workspaceSnapshotImage();
                    break;
                case 'preference':
                    if (payload.key === 'precision') this.$set(this.workspacePreferences, 'precisionMode', payload.value == null ? 'auto' : 'manual');
                    this.$set(
                        this.workspacePreferences,
                        payload.key,
                        payload.value
                    );
                    localStorage.setItem(
                        'tc_workspace_preferences',
                        JSON.stringify(this.workspacePreferences)
                    );
                    this.workspaceApplyPreferences();
                    break;
                case 'toggleObject':
                case 'lockObject':
                case 'deleteObject': {
                    const [side, index] = payload.split(':');
                    const o = source?.[side]?.[Number(index)];
                    if (!o) return;
                    if (action === 'deleteObject')
                        source[side].splice(Number(index), 1);
                    else
                        this.$set(
                            o.settings,
                            action === 'lockObject' ? '$locked' : 'display',
                            action === 'lockObject'
                                ? !o.settings.$locked
                                : o.settings.display === false
                        );
                    this.workspaceDataChanged();
                    break;
                }
                case 'toggleAllObjects': {
                    const all = ['onchart', 'offchart'].flatMap(
                        (side) => source?.[side] || []
                    );
                    const show = all.some((o) => o.settings?.display === false);
                    all.forEach((o) => this.$set(o.settings, 'display', show));
                    this.workspaceDataChanged();
                    break;
                }
                case 'undo':
                case 'redo':
                    this.workspaceRestoreHistory(action === 'undo' ? -1 : 1);
                    break;
                case 'saveWorkspace':
                    return this.workspaceSave(payload);
                case 'refreshWorkspaces':
                    return this.workspaceRefreshCloud();
                case 'resolveWorkspaceConflict':
                    return this.workspaceResolveConflict(payload);
                case 'loadWorkspace':
                    this.workspaceLoad(
                        this.workspaceItems.find((x) => x.id === payload)
                    );
                    break;
                case 'deleteWorkspace':
                    return this.workspaceDelete(payload);
                case 'exportWorkspace':
                    this.workspaceDownload(
                        JSON.stringify(
                            this.workspaceMakeSnapshot(
                                this.workspaceName || 'Untitled'
                            ),
                            null,
                            2
                        ),
                        'tradingcandle-workspace.json',
                        'application/json'
                    );
                    break;
                case 'importWorkspace':
                    this.workspaceImport(payload);
                    break;
                case 'createAlert':
                    return this.workspaceCreateAlert(payload);
                case 'editAlert':
                    return this.workspaceSubmitEditAlert(payload);
                case 'toggleAlert':
                    return this.workspaceToggleAlert(payload);
                case 'deleteAlert':
                    return this.workspaceDeleteAlert(payload);
                case 'replay':
                    this.workspaceStartReplay();
                    break;
                case 'replaySeek':
                    this.workspaceReplay.playing = false;
                    this.workspaceReplay.index = payload;
                    this.workspaceRenderReplay();
                    break;
                case 'replayStep':
                    this.workspaceReplay.index = Math.min(
                        this.workspaceReplay.total,
                        this.workspaceReplay.index + 1
                    );
                    this.workspaceRenderReplay();
                    break;
                case 'replayPlay':
                    this.workspaceReplay.playing =
                        !this.workspaceReplay.playing;
                    break;
                case 'replaySpeed':
                    this.workspaceReplay.speed = payload;
                    break;
                case 'replayExit':
                    this.workspaceReplay.active = false;
                    this.workspaceReplay.playing = false;
                    this.workspaceReplayCube = null;
                    this.$nextTick(()=>{if(this._workspaceLiveRange)this.$refs.tv?.setRange(...this._workspaceLiveRange)});
                    break;
                case 'addComparison':
                    this.workspaceCompare(payload);
                    break;
                case 'removeComparison':
                    if (source)
                        source.onchart = source.onchart.filter(
                            (o) => o.settings?.$workspaceComparison !== payload
                        );
                    this.workspaceDataChanged();
                    break;
                case 'paperOrder':
                    this.workspaceOrder(payload);
                    break;
                case 'cancelPaperOrder':
                    this.workspacePaper.orders =
                        this.workspacePaper.orders.filter(
                            (o) => o.id !== payload
                        );
                    this.workspacePersist();
                    break;
                case 'closePaperPosition': {
                    const p = this.workspacePaper.positions[payload];
                    if (!p || payload !== this.workspaceKey)
                        throw new Error(
                            'Open the position symbol before closing it.'
                        );
                    this.workspaceOrder({
                        type: 'market',
                        side: p.quantity > 0 ? 'sell' : 'buy',
                        quantity: Math.abs(p.quantity),
                    });
                    break;
                }
            }
        },
        workspaceDownload(text, name, type) {
            const url = URL.createObjectURL(new Blob([text], { type }));
            const link = document.createElement('a');
            link.href = url;
            link.download = name;
            link.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        },
        workspaceSnapshotImage() {
            const element = this.$refs.workspaceChartGrid;
            const canvases = Array.from(
                element?.querySelectorAll('canvas') || []
            );
            if (!canvases.length)
                throw new Error('Load a chart before taking a snapshot.');
            const bounds = element.getBoundingClientRect();
            const canvas = document.createElement('canvas');
            canvas.width = bounds.width * 2;
            canvas.height = bounds.height * 2;
            const ctx = canvas.getContext('2d');
            ctx.scale(2, 2);
            ctx.fillStyle = this.colors.colorBack;
            ctx.fillRect(0, 0, bounds.width, bounds.height);
            canvases.forEach((c) => {
                const r = c.getBoundingClientRect();
                ctx.drawImage(
                    c,
                    r.left - bounds.left,
                    r.top - bounds.top,
                    r.width,
                    r.height
                );
            });
            canvas.toBlob((blob) => {
                if (!blob) return;
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = this.symbol + '-chart.png';
                a.click();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            });
        },
        workspaceApplyPreferences() {
            for (const dc of Object.values(this.chart)) {
                if (!dc?.data?.chart) continue;
                const c = dc.data.chart;
                this.$set(c, 'symbolInfo', this.workspaceSymbolInfo);
                const p = this.workspacePreferences;
                for (const [key, value] of Object.entries({
                    colorCandleUp: p.up,
                    colorCandleDw: p.down,
                    upBarColor: p.up,
                    downBarColor: p.down,
                    showVolume: p.volume,
                    priceLine: p.priceLine,
                }))
                    this.$set(c.settings, key, value);
                if (!c.grid) this.$set(c, 'grid', {});
                this.$set(c.grid, 'logScale', p.log);
            }
            this.$set(
                this.chart_config,
                'PRICE_PRECISION',
                this.workspacePricePrecision
            );
            this.workspaceDataChanged();
        },
        workspaceMakeSnapshot(name) {
            if (!this.workspaceSource)
                throw new Error('Load chart data first.');
            return workspaceSnapshot(this.workspaceSource, {
                name,
                symbol: this.symbol,
                pairId: this.pairId,
                connector: this.connector,
                exchange: this.exchange,
                category: this.category,
                timeframe: this.timeframe,
                type: this.currentChartType,
                layout: this.workspaceLayout,
                secondaryTf: this.workspaceSecondaryTf,
                panes: Object.fromEntries(Object.entries(this.workspacePaneCubes)
                    .filter(([key]) => key.startsWith(this.workspaceKey + ':pane:'))
                    .map(([key, cube]) => [key, workspaceSnapshot(cube.data, {name:'Chart pane'})])),
                preferences: {...this.workspacePreferences, ui:{
                    vtEnabled:this.workspaceVTEnabled, night:this.night, timezone:this.chartTimezone,
                    showDrawingTools:this.showDrawingTools, drawingRailExpanded:this.drawingRailExpanded,
                    watchlistVisible:this.watchlistVisible, watchlistPanelWidth:this.watchlistPanelWidth,
                    secondaryTf:this.workspaceSecondaryTf,
                }},
            });
        },
        async workspaceSave(name) {
            name = String(name || '').trim();
            if (!name) throw new Error('Name this workspace.');
            const snapshot = this.workspaceMakeSnapshot(name);
            const previous = this.workspaceItems.find((x) => x.name === name);
            snapshot.id = previous?.id || Date.now().toString();
            snapshot.cloudRevision = previous?.cloudRevision || 0;
            snapshot.cloudPending = true;
            this.workspaceItems = [
                snapshot,
                ...this.workspaceItems.filter((x) => x.id !== snapshot.id),
            ].slice(0, 20);
            this.workspaceName = name;
            this.workspaceSaved = true;
            this.workspacePersist();
            this.workspaceCloudBusy = true;
            const scope = this.workspaceScope;
            try {
                const { item } = await this.workspaceAccountRequest('/' + encodeURIComponent(snapshot.id), 'PUT', {
                    snapshot, revision: snapshot.cloudRevision,
                });
                if (scope !== this.workspaceScope || this._isDestroyed) return;
                this.workspaceItems = this.workspaceItems.map(saved => saved.id === item.id ? item : saved);
                this.workspacePersist();
                this.workspaceCloudStatus = 'Saved to your account';
                this.workspaceNotify('Workspace saved to your account.');
            } catch (error) {
                this.workspaceCloudStatus = 'Local copy saved; account save failed';
                this.workspaceNotify('Saved locally. ' + error.message, 'alert');
            } finally { this.workspaceCloudBusy = false; }
        },
        async workspaceLoadEarlier(quiet = false) {
            if (!supportsHistory(this.connector)) throw new Error('Earlier history is unavailable for this provider.');
            if (this.workspaceReplay.active) throw new Error('Exit replay before loading earlier bars.');
            if (this._workspaceHistoryLoading) return;
            const displayTf = this.timeframe;
            // A custom timeframe (e.g. "4m") isn't a real provider interval — it's
            // derived client-side from a loaded base timeframe. Fetching earlier
            // history must therefore page the base timeframe's own candles and
            // re-derive the custom view, instead of asking the broker for an
            // interval it has never heard of.
            const customTimeframe = this.getCustomTimeframe(displayTf);
            const timeframe = customTimeframe
                ? (this.getBestLoadedBaseTimeframe(customTimeframe) || this.getNearestBaseTimeframe(customTimeframe))
                : displayTf;
            if (customTimeframe && !timeframe) throw new Error('No suitable base timeframe is loaded for this custom timeframe.');
            const baseCube = customTimeframe ? this.chart[timeframe] : this.chart[displayTf];
            const baseCandles = customTimeframe
                ? (baseCube?.data?.chart?.data || this.dataset[timeframe]?.chart?.data || [])
                : this.workspaceCandles;
            const first = baseCandles[0];
            if (!first) throw new Error('Load chart data first.');
            if (this._workspaceHistoryEnd === this.workspaceKey + ":" + timeframe + ":" + first[0]) return;
            const key = this.workspaceKey, cube = this.chart[timeframe];
            this._workspaceHistoryLoading = true;
            try {
                const candles = await fetchEarlierHistory({ connector: this.connector, symbol: this.symbol, pairId: this.pairId, exchange: this.exchange, timeframe }, first[0]);
                if (key !== this.workspaceKey || displayTf !== this.timeframe || cube !== this.chart[timeframe]) return;
                const previousLength = baseCandles.length;
                const merged = mergeHistory(candles, baseCandles);
                const view = this.$refs.tv, range = view?.getRange();
                this.$set(cube.data.chart, 'data', merged);
                if (this.dataset[timeframe]?.chart) this.$set(this.dataset[timeframe].chart, 'data', merged);
                if (merged?.length && merged[0]?.[0] !== undefined && first?.[0] !== undefined && merged[0][0] >= first[0]) this._workspaceHistoryEnd = key + ':' + timeframe + ':' + merged[0][0];
                if (customTimeframe) {
                    // Re-derive in place rather than via _buildCustomTimeframeChart,
                    // which remounts the chart (new :key) and would drop the user's
                    // current pan/zoom right after they panned to load this history.
                    const derived = this.deriveCustomTimeframeData(customTimeframe);
                    const displayCube = this.chart[displayTf];
                    if (displayCube?.data?.chart) this.$set(displayCube.data.chart, 'data', derived);
                    if (this.dataset[displayTf]?.chart) this.$set(this.dataset[displayTf].chart, 'data', derived);
                }
                if (!quiet) this.workspaceNotify(`${merged.length - previousLength} earlier bars loaded.`);
                await this.$nextTick();
                if (view === this.$refs.tv && range?.[1] && displayTf === this.timeframe) view.setRange(...range);
                this.refreshCurrentIndicators(displayTf);
            } finally { this._workspaceHistoryLoading = false; }
        },
        async workspaceAccountRequest(path = '', method = 'GET', body) {
            const userId = this.$clerk.user?.id;
            const token = await this.$clerk.getToken();
            if (!token || !userId || userId !== this.$clerk.user?.id) throw new Error('Sign in to save account workspaces.');
            const response = await fetch(`${process.env.VUE_APP_API_BASE || ''}/api/chart/workspaces${path}`, {
                method, headers: { Authorization: `Bearer ${token}`, Accept: 'application/json', 'Content-Type': 'application/json' },
                ...(body ? { body: JSON.stringify(body) } : {}),
            });
            const text = await response.text();
            let result;
            try { result = JSON.parse(text); }
            catch (_) { throw new Error('Account workspace service is unavailable. Your local workspaces are still available. Please retry when the service is back online.'); }
            if (!response.ok) throw new Error(result?.error || 'Account workspace request failed.');
            if (!result || (method === 'GET' && !Array.isArray(result.items))) throw new Error('The account workspace service returned an invalid response. Your local workspaces are still available.');
            return result;
        },
        async workspaceSyncCurrent() {
            const scope=this.workspaceScope;
            if(!this.$clerk?.user?.id || this._currentSyncBusy || this.protectChart || this.workspaceReplay.active || this._workspacePending) return;
            if(this._currentReadyScope!==scope) { await this.workspaceRefreshCloud(); return; }
            if(this.workspaceCloudBusy || !this.workspaceCandles.length || this._currentConflict) return;
            if([...(this.workspaceSource?.onchart || []),...(this.workspaceSource?.offchart || [])].some(overlay=>overlay.loading))return;
            const snapshot=this.workspaceMakeSnapshot('Current chart');
            const fingerprint=JSON.stringify(snapshot,(key,value)=>key==='savedAt'?0:value);
            if(fingerprint===this._currentFingerprint)return;
            this._currentSyncBusy=true;
            try {
                const {item}=await this.workspaceAccountRequest('/current', 'PUT', {snapshot,revision:this._currentRevision || 0});
                if(scope!==this.workspaceScope)return;
                this._currentRevision=item.cloudRevision;
                this._currentFingerprint=fingerprint;
                this.workspaceCloudStatus='Chart synced to your account';
            } catch(error) {
                if(scope!==this.workspaceScope)return;
                this.workspaceCloudStatus='Chart sync failed: '+error.message;
                if(/another device|changed|409/i.test(error.message)) {
                    this._currentConflict=true;
                    this.workspaceNotify('Chart changed on another device. Refresh account workspaces before saving local changes.', 'alert');
                }
            } finally { this._currentSyncBusy=false; }
        },
        async workspaceRefreshCloud() {
            if (this.workspaceCloudBusy || !this.$clerk.user?.id) return;
            this.workspaceCloudBusy = true;
            const scope = this.workspaceScope;
            try {
                const { items } = await this.workspaceAccountRequest();
                if (scope !== this.workspaceScope || this._isDestroyed) return;
                if(this._currentReadyScope!==scope) {
                    const current=items.find(item=>item.id==='current');
                    this._currentRevision=current?.cloudRevision || 0;
                    this._currentReadyScope=scope;
                    if(current) {
                        this._currentRestoring=true;
                        this.workspaceLoad(current);
                    }
                }
                this._currentConflict=false;
                const local = this.workspaceItems.filter(item => item.cloudPending || !item.cloudRevision);
                const ids = new Set(local.map(item => item.id));
                this.workspaceItems = [...local, ...items.filter(item => !ids.has(item.id))];
                this.workspaceCloudStatus = 'Account workspaces refreshed';
                this.workspacePersist();
            } catch (error) { this.workspaceCloudStatus = error.message; }
            finally { this.workspaceCloudBusy = false; }
        },
        async workspaceResolveConflict(id) {
            const item = this.workspaceItems.find(item => item.id === id);
            if (!item?.cloudPending) return;
            const copy = { ...clone(item), id: Date.now().toString() + '_copy',
                name: (item.name + ' (local copy)').slice(0,100), cloudRevision: 0, cloudPending: true };
            this.workspaceItems = [copy, ...this.workspaceItems.filter(item => item.id !== id)];
            this.workspacePersist();
            await this.workspaceRefreshCloud();
            this.workspaceNotify('Local edits preserved as a separate copy. Account version refreshed.');
        },
        async workspaceDelete(id) {
            const item = this.workspaceItems.find(item => item.id === id);
            if (!item) return;
            if (item.cloudRevision) {
                await this.workspaceAccountRequest('/' + encodeURIComponent(id), 'DELETE', { revision: item.cloudRevision });
            }
            this.workspaceItems = this.workspaceItems.filter(item => item.id !== id);
            this.workspacePersist();
        },
        async workspaceImport(file) {
            try {
                if (!file) return;
                if (file.size > 2000000)
                    throw new Error('Workspace file exceeds 2 MB.');
                const item = validateWorkspace(JSON.parse(await file.text()));
                item.id = Date.now().toString();
                delete item.cloudRevision;
                delete item.cloudPending;
                this.workspaceItems.unshift(item);
                this.workspacePersist();
                this.workspaceNotify(
                    'Workspace imported. Open it from your workspaces.'
                );
            } catch (e) {
                this.workspaceNotify(e.message, 'alert');
            }
        },
        workspaceLoad(item) {
            item = validateWorkspace(item);
            this.workspaceDispatch('replayExit');
            this._workspacePending = item;
            this.workspacePanel = null;
            this.workspaceName = item.name;
            this.workspaceLayout = [1, 2, 4].includes(item.layout)
                ? item.layout
                : 1;
            this.workspaceSavedPanes = item.panes || {};
            this.workspacePaneCubes = {};
            this.workspaceResetActivePane(0);
            this.workspaceSecondaryTf = item.secondaryTf || ['15m', '1H', '1D'];
            Object.assign(this, {
                symbol: item.symbol,
                pairId: item.pairId || item.symbol,
                query: item.symbol,
                connector: item.connector,
                exchange: item.exchange,
                category: item.category,
                timeframe: item.timeframe,
            });
            this._workspacePendingCube = this.chart[this.timeframe];
            this.loadChart();
        },
        workspaceFinishLoad() {
            const item = this._workspacePending;
            const data = this.workspaceSource;
            if (
                !item ||
                !data?.chart?.data?.length ||
                this.chart[this.timeframe] === this._workspacePendingCube
            )
                return;
            this.$set(data, 'onchart', clone(item.onchart));
            this.$set(data, 'offchart', clone(item.offchart));
            this.$set(data, 'drawingPrefs', clone(item.drawingPrefs || {}));
            this.$set(data.chart, 'settings', clone(item.chartSettings));
            this.$set(data.chart, 'grid', clone(item.chartGrid));
            this.workspacePreferences = {
                ...this.workspacePreferences,
                ...item.preferences,
            };
            const ui=item.preferences?.ui;
            if(ui) {
                if(typeof ui.vtEnabled==='boolean')this.workspaceVTEnabled=ui.vtEnabled;
                if(typeof ui.night==='boolean')this.night=ui.night;
                if(typeof ui.timezone==='string')this.chartTimezone=ui.timezone;
                for(const key of ['showDrawingTools','drawingRailExpanded','watchlistVisible'])if(typeof ui[key]==='boolean')this[key]=ui[key];
                if(Number.isFinite(ui.watchlistPanelWidth))this.watchlistPanelWidth=Math.max(280,Math.min(600,ui.watchlistPanelWidth));
            }
            this.currentChartType = item.type || 'candles';
            this.updateMainChartType(this.currentChartType);
            this._workspacePending = null;
            this.resetkey++;
            this.workspaceApplyPreferences();
            this.workspaceSaved = true;
            if(this._currentRestoring) {
                this._currentRestoring=false;
                this._currentFingerprint=JSON.stringify(this.workspaceMakeSnapshot('Current chart'),(key,value)=>key==='savedAt'?0:value);
            }
            this.workspaceNotify('Workspace restored.');
        },
        workspaceCaptureHistory() {
            const source = this.workspaceSelectedSource;
            if (!source) return;
            if (
                ['onchart', 'offchart'].some((side) =>
                    (source[side] || []).some(
                        (o) => o.settings?.$state === 'wip'
                    )
                )
            )
                return;
            const drawings = JSON.stringify(
                ['onchart', 'offchart'].map((side) =>
                    (source[side] || []).filter(isDrawing).map((overlay) => {
                        const copy = clone(overlay);
                        delete copy.id;
                        delete copy.settings.$selected;
                        return copy;
                    })
                )
            );
            if (drawings === this._workspaceLastHistory) return;
            if (this._workspaceLastHistory != null) this.workspaceSaved = false;
            this._workspaceLastHistory = drawings;
            this.workspaceHistory = this.workspaceHistory.slice(
                0,
                this.workspaceHistoryIndex + 1
            );
            this.workspaceHistory.push(drawings);
            if (this.workspaceHistory.length > 50)
                this.workspaceHistory.shift();
            this.workspaceHistoryIndex = this.workspaceHistory.length - 1;
        },
        workspaceRestoreHistory(delta) {
            const index = this.workspaceHistoryIndex + delta;
            if (
                index < 0 ||
                index >= this.workspaceHistory.length ||
                !this.workspaceSelectedSource
            )
                return;
            const drawings = JSON.parse(this.workspaceHistory[index]);
            ['onchart', 'offchart'].forEach((side, i) =>
                this.$set(this.workspaceSelectedSource, side, [
                    ...(this.workspaceSelectedSource[side] || []).filter(
                        (o) => !isDrawing(o)
                    ),
                    ...drawings[i],
                ])
            );
            this.workspaceHistoryIndex = index;
            this._workspaceLastHistory = this.workspaceHistory[index];
            this.workspaceDataChanged();
            this.resetkey++;
        },
        workspaceStartReplay() {
            if (this.workspaceCandles.length < 2)
                throw new Error('Load at least two bars first.');
            this.workspacePanel = null;
            this.workspaceReplay = {
                active: true,
                playing: false,
                index: Math.max(1, this.workspaceCandles.length - 80),
                total: this.workspaceCandles.length,
                speed: 1,
                time: '',
            };
            this._workspaceLiveRange = this.$refs.tv?.getRange()?.slice();
            this._workspaceReplaySource = clone(this.workspaceSource);
            this.workspaceRenderReplay();
            this.workspaceNotify(
                'Replay uses loaded bars. Studies can repaint; do not use it as a backtest.',
                'replay'
            );
        },
        workspaceRenderReplay() {
            const data = replayData(
                this._workspaceReplaySource,
                this.workspaceReplay.index
            );
            if (!this.workspaceReplayCube) {
                this.workspaceReplayCube = new DataCube(data, { scripts: false });
            } else {
                // Reuse one cube: recreating it on every step adds watchers to
                // the same chart instance and discards drawings made in replay.
                const cube = this.workspaceReplayCube;
                this.$set(cube.data.chart, 'data', data.chart.data);
                for (const side of ['onchart', 'offchart']) {
                    this.$set(cube.data, side, [
                        ...data[side],
                        ...(cube.data[side] || []).filter(isDrawing),
                    ]);
                }
            }
            this.workspaceReplay.time = new Date(
                data.chart.data[data.chart.data.length - 1][0]
            ).toLocaleString();
            // Follow the replay cursor explicitly; live updates never own this viewport.
            this.$nextTick(() => this.$nextTick(() => {
                if (!this.workspaceReplay.active) return;
                const rows=data.chart.data, last=rows[rows.length-1]?.[0];
                const step=this.timeframeToMs(this.timeframe);
                const current=this.$refs.tv?.getRange();
                const span=current?.length===2 ? current[1]-current[0] : step*100;
                const view=this.$refs.tv, chart=view?.$refs.chart;
                if (!chart || !Number.isFinite(last) || step<=0) return;
                if (view.chart_props.ib) {
                    const right=rows.length-1+20;
                    chart.setRange(right-Math.max(40,span/step),right);
                } else view.setRange(last+step*20-Math.max(span,step*40),last+step*20);
            }));
            if (this.workspaceReplay.index >= this.workspaceReplay.total)
                this.workspaceReplay.playing = false;
        },
        async workspaceCompare(symbol) {
            try {
                symbol = String(symbol || '')
                    .trim()
                    .toUpperCase();
                if (!/^[A-Z0-9]{3,30}(\.P)?$/.test(symbol))
                    throw new Error('Enter a Binance symbol, such as ETHUSDT.');
                if (this.workspaceReplay.active)
                    throw new Error('Exit replay before adding a comparison.');
                const key = this.workspaceKey,
                    tf = this.timeframe;
                this.workspaceComparing = true;
                const response = await binance.loadKlineFromBinance(symbol, tf);
                if (!response.ok)
                    throw new Error(
                        'This symbol or interval is unavailable from Binance.'
                    );
                const rows = await response.json();
                if (key !== this.workspaceKey || tf !== this.timeframe) return;
                const data = normalizedComparison(
                    this.workspaceCandles,
                    rows.map((r) => r.map(Number))
                );
                this.workspaceSource.onchart =
                    this.workspaceSource.onchart.filter(
                        (o) => o.settings?.$workspaceComparison !== symbol
                    );
                this.workspaceSource.onchart.push({
                    name: symbol + ' · rebased',
                    type: 'Spline',
                    data,
                    settings: {
                        $workspaceComparison: symbol,
                        color: '#a78bfa',
                        lineWidth: 2,
                    },
                });
                this.workspaceDataChanged();
            } catch (e) {
                this.workspaceNotify(e.message, 'alert');
            } finally {
                this.workspaceComparing = false;
            }
        },
        workspaceOrder(form) {
            if (this.workspaceReplay.active)
                throw new Error('Exit replay to place paper orders.');
            if (!this.workspacePrice)
                throw new Error('No current price is available.');
            const order = {
                ...form,
                id: Date.now() + '-' + Math.random(),
                key: this.workspaceKey,
                symbol: this.symbol,
            };
            if (form.type === 'market')
                this.workspacePaper = fillPaperOrder(
                    this.workspacePaper,
                    order,
                    this.workspacePrice,
                    Date.now()
                );
            else {
                if (
                    !Number.isFinite(form.price) ||
                    form.price <= 0 ||
                    !Number.isFinite(form.quantity) ||
                    form.quantity <= 0
                )
                    throw new Error('Enter positive quantity and price.');
                this.workspacePaper.orders.push(order);
            }
            this.workspacePersist();
            this.workspaceNotify(
                'Simulated ' +
                    form.side +
                    ' order ' +
                    (form.type === 'market' ? 'filled.' : 'placed.'),
                'trade'
            );
        },
        workspaceRange(range, pane = 0) {
            if (this._workspaceSyncingRange) return;
            if (!Array.isArray(range) || !Number.isFinite(range[0])) return;
            if (pane === 0) {
                const bars = this.workspaceCandles;
                const threshold = bars[Math.min(100, Math.max(0, bars.length - 1))]?.[0];
                if (!this.workspaceRangeBusy && !this._workspaceHistoryLoading && supportsHistory(this.connector) && !this.workspaceReplay.active && bars.length && threshold !== undefined && range[0] <= threshold && Date.now() > (this._historyRetryAt || 0)) {
                    this.workspaceLoadEarlier(true).catch(error => {
                        this._historyRetryAt = Date.now() + 15000;
                        this.workspaceNotify(error.message, 'alert');
                    });
                }
            }
            if (!this.workspaceSyncRange) return;
            const previous = this._workspaceSyncedRange;
            if (previous && previous[0] === range[0] && previous[1] === range[1]) return;
            this._workspaceSyncedRange = range.slice();
            this._workspaceSyncingRange = true;
            const secondary = this.$refs.workspaceSecondary || [];
            const views = [this.$refs.tv, ...(Array.isArray(secondary) ? secondary : [secondary])];
            views.forEach((view, index) => {
                const indexOfPane = index === 0 ? 0 : view.pane;
                if (indexOfPane !== pane) view?.setRange(...range);
            });
            this.$nextTick(() => { this._workspaceSyncingRange = false });
        },
        workspaceKeydown(event) {
            if (
                event.target.closest(
                    'input,textarea,select,[contenteditable="true"]'
                )
            )
                return;
            let action;
            if (
                (event.metaKey || event.ctrlKey) &&
                event.key.toLowerCase() === 'k'
            )
                action = 'commands';
            else if (
                (event.metaKey || event.ctrlKey) &&
                event.key.toLowerCase() === 'z'
            )
                action = event.shiftKey ? 'redo' : 'undo';
            else if (event.altKey && event.key.toLowerCase() === 'a')
                action = 'alerts';
            else if (event.key === '/') action = 'symbol';
            else if (event.key === 'Escape') action = 'close';
            if (action) {
                event.preventDefault();
                this.workspaceAction({ action });
            }
        },
        workspacePulse() {
            this.workspaceTick++;
            this.workspaceFinishLoad();
            if (
                this.workspaceReplay.playing &&
                Date.now() - (this._workspaceReplayLast || 0) >=
                    1000 / this.workspaceReplay.speed
            ) {
                this._workspaceReplayLast = Date.now();
                this.workspaceDispatch('replayStep');
            }
            if (!this.workspaceReplay.active) this.workspaceCaptureHistory();
            const price = this.workspacePrice;
            if (!price || this.workspaceReplay.active) return;
            const previous = this._workspacePrices?.[this.workspaceKey];
            if (!this._workspacePrices) this._workspacePrices = {};
            this._workspacePrices[this.workspaceKey] = price;
            const result = evaluateAlerts(
                this.workspaceAlerts,
                this.workspaceKey,
                previous,
                price,
                Date.now()
            );
            if (result.fired.length) {
                this.workspaceAlerts = result.alerts;
                this.workspaceRecordAlertTriggers(result.fired);
                this.workspaceNotify(
                    result.fired
                        .map((a) => a.name + ' triggered at ' + price)
                        .join(' · '),
                    'alert'
                );
                this.workspacePersist();
            }
            if (price !== previous) {
                this.workspacePaper = processPaperOrders(
                    this.workspacePaper,
                    this.workspaceKey,
                    price,
                    Date.now()
                );
                this.workspacePersist();
            }
        },
    },
    watch: {
        workspaceLayout(value) { if (this.workspaceActivePane >= value) this.workspaceResetActivePane(0) },
        workspaceSource: {
            immediate: true,
            handler(source) {
                // A new timeframe cube needs metadata even when its numeric
                // precision matches the previous one (the precision watcher
                // does not fire in that case).
                if (source?.chart && source.chart.symbolInfo !== this.workspaceSymbolInfo) {
                    this.$set(source.chart, 'symbolInfo', this.workspaceSymbolInfo);
                }
            },
        },
        workspaceSymbolInfo: {
            handler(info) {
                for (const dc of Object.values(this.chart || {})) {
                    if (dc?.data?.chart && dc.data.chart.symbolInfo !== info) this.$set(dc.data.chart, 'symbolInfo', info);
                }
            },
        },
        workspacePricePrecision: {
            immediate: true,
            handler(value) {
                if (this.chart_config) this.$set(this.chart_config, 'PRICE_PRECISION', value);
                for (const dc of Object.values(this.chart || {})) {
                    if (dc?.data?.chart) this.$set(dc.data.chart, 'symbolInfo', this.workspaceSymbolInfo);
                }
            },
        },
        workspaceScope() {
            this._currentReadyScope=null; this._currentFingerprint=null; this._currentRevision=0; this._currentConflict=false;
            this.workspacePaneCubes = {};
            this.workspaceSavedPanes = {};
            this.workspaceResetActivePane(0);
            const saved = read(this.workspaceScope, {});
            this.workspaceItems = saved.items || [];
            this.workspaceAlerts = saved.alerts || [];
            this.workspaceAlertLog = saved.alertLog || [];
            this.workspacePaper = saved.paper || newPaperAccount();
            this.workspaceCloudStatus = '';
            this.workspaceRefreshCloud();
        },
        workspaceKey() {
            this.workspaceHistory = [];
            this.workspaceHistoryIndex = -1;
            this._workspaceLastHistory = null;
            if (this.workspaceReplay.active)
                this.workspaceDispatch('replayExit');
        },
        timeframe() {
            if (this.workspaceReplay.active)
                this.workspaceDispatch('replayExit');
            this.workspaceHistory = [];
            this.workspaceHistoryIndex = -1;
            this._workspaceLastHistory = null;
        },
        'workspaceCandles.length'() {
            this.workspaceApplyPreferences();
        },
    },
    mounted() {
        const saved = read(this.workspaceScope, {});
        this.workspaceItems = saved.items || [];
        this.workspaceAlerts = saved.alerts || [];
        this.workspaceAlertLog = saved.alertLog || [];
        this.workspacePaper = saved.paper || newPaperAccount();
        this.workspaceRefreshCloud();
        this.workspaceRefreshServerAlerts();
        this._currentSyncTimer=setInterval(()=>this.workspaceSyncCurrent(),2000);
        this._workspaceTimer = setInterval(() => this.workspacePulse(), 250);
        this._workspaceAlertsTimer = setInterval(
            () => this.workspaceRefreshServerAlerts(),
            20000
        );
        window.addEventListener('keydown', this.workspaceKeydown);
    },
    beforeDestroy() {
        clearInterval(this._currentSyncTimer);
        clearInterval(this._workspaceTimer);
        clearInterval(this._workspaceAlertsTimer);
        clearTimeout(this._workspaceToastTimer);
        window.removeEventListener('keydown', this.workspaceKeydown);
    },
};
